// ── Pure utility functions for PMJournal ─────────────────────────────────────
// Extracted here so they can be tested without mounting React components.

import { IProject, IProjectAndActivity } from '../../interface/IProjects'
import { PMJournalHeaderType, ListFilter, AI_JOURNAL, YE_JOURNAL, EMPTY_GUID } from './PMJournal.types'

// ── Activity filter ───────────────────────────────────────────────────────────

/**
 * Returns the activities available for a project, filtered by journal type.
 * - AI_JOURNAL  → only activities whose category is 'AI'
 * - YE_JOURNAL  → Billable activities, excluding AI and 'non-bill hours' categories
 * - other       → all activities
 */
export const getFilteredActivities = (
    journalType: string,
    projects: IProject[],
    projectId: string,
    journalDate: Date | null,
): IProjectAndActivity[] => {
    const project = projects.find((p) => p.mserp_projectid === projectId)
    if (!project?.activities) return []

    const dateStr = journalDate ? journalDate.toISOString().slice(0, 10) : null

    const isDateValid = (a: IProjectAndActivity): boolean => {
        if (!dateStr) return true
        if (!a.taskStartDate && !a.taskFinishDate) return false
        const start = a.taskStartDate ?? '0001-01-01'
        const finish = a.taskFinishDate ?? '9999-12-31'
        return dateStr >= start && dateStr <= finish
    }

    const isValid = (a: IProjectAndActivity): boolean =>
        !!a.mserp_description && isDateValid(a)

    if (journalType === YE_JOURNAL)
        return project.activities.filter(
            (a) =>
                isValid(a) &&
                a.mserp_linepropertyid === 'Billable' &&
                !!a.mserp_category &&
                a.mserp_category !== 'AI' &&
                a.mserp_category !== 'Article+Expenses' &&
                a.mserp_category !== 'Non-billable hours',
        )

    if (journalType === AI_JOURNAL)
        return project.activities.filter(
            (a) => isValid(a) && a.mserp_category === 'AI',
        )

    return project.activities.filter(isValid)
}

// ── Project filter ───────────────────────────────────────────────────────────

/**
 * Filters the project list to those whose date range covers journalDate.
 * Projects without any date range are always included.
 */
export const getFilteredProjects = (
    projects: IProject[],
    journalDate: Date | null,
): IProject[] => {
    if (!journalDate) return projects
    const dateStr = journalDate.toISOString().slice(0, 10)
    return projects.filter((p) => {
        if (!p.projectStartDate && !p.projectEndDate) return true
        const start = p.projectStartDate ?? '0001-01-01'
        const end = p.projectEndDate ?? '9999-12-31'
        return dateStr >= start && dateStr <= end
    })
}

// ── Journal list filter ───────────────────────────────────────────────────────

/**
 * Filters a list of journal headers by the active ListFilter.
 * `projectNameFn` resolves a projectId to a display name used by the name filter.
 */
export const filterJournals = (
    journals: PMJournalHeaderType[],
    filter: ListFilter,
    projectNameFn: (id: string) => string,
): PMJournalHeaderType[] =>
    journals
        .filter((j) => {
            const matchId = !filter.journalId || (j.batchnumber ?? '').toLowerCase().includes(filter.journalId.toLowerCase())
            const matchProject = !filter.project || projectNameFn(j.projectId).toLowerCase().includes(filter.project.toLowerCase())
            const matchType = !filter.type || j.journalName === filter.type
            const isSaved = !!(j.projJournalTableEntityId && j.projJournalTableEntityId !== EMPTY_GUID)
            const matchStatus =
                !filter.status ||
                (filter.status === 'posted'   ? j.posted :
                 filter.status === 'saved'    ? !j.posted && isSaved :
                 filter.status === 'unposted' ? !j.posted && !isSaved : true)
            const matchRole = !filter.role || (filter.role === 'deputy' ? j.isDeputyView : !j.isDeputyView)
            return matchId && matchProject && matchType && matchStatus && matchRole
        })

// ── Violation code → i18n key ─────────────────────────────────────────────────

/**
 * Maps a backend VIOLATION_CODE to the corresponding i18n key,
 * or `null` for unknown codes (the caller should use a generic fallback).
 */
export const violationKey = (code: number | undefined): string | null => {
    switch (code) {
        case 9302: return 'pmJournal.errorNotPermittedPeriod'
        case 9501: return 'pmJournal.errorDateBeforeFrom'
        default: return null
    }
}
